import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError, inr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CurrencyInr, ArrowClockwise, Plug, Plus, Sparkle, MagnifyingGlass, TrendUp, ChartBar, Receipt, ArrowSquareOut } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from "recharts";
import { toast } from "sonner";

const METHOD_TONES = {
  UPI:        "bg-emerald-50 text-emerald-800 border-emerald-200",
  Cash:       "bg-amber-50 text-amber-800 border-amber-200",
  Card:       "bg-blue-50 text-blue-800 border-blue-200",
  Cheque:     "bg-slate-100 text-slate-800 border-slate-200",
  NetBanking: "bg-indigo-50 text-indigo-800 border-indigo-200",
  Wallet:     "bg-purple-50 text-purple-800 border-purple-200",
  Other:      "bg-slate-100 text-slate-700 border-slate-200",
};

const STATUS_TONES = {
  Success:  "bg-emerald-50 text-emerald-700 border-emerald-200",
  Pending:  "bg-amber-50 text-amber-800 border-amber-200",
  Failed:   "bg-red-50 text-red-700 border-red-200",
  Refunded: "bg-slate-100 text-slate-700 border-slate-200",
};

const PROVIDER_COLOR = {
  razorpay: "#3B82F6",
  cashfree: "#10B981",
  phonepe:  "#7C3AED",
  paytm:    "#0EA5E9",
  bharatpe: "#F59E0B",
  manual:   "#64748B",
};

export default function PaymentCenter() {
  const [tab, setTab] = useState("dashboard"); // dashboard | transactions | providers | reports

  return (
    <div className="max-w-[1500px] mx-auto p-6" data-testid="payment-center-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <CurrencyInr size={14} weight="fill" className="text-orange-500" />
          <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500">Business Payment Center</span>
        </div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Payment Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Live collection · UPI / Cash / Card / Wallet · Razorpay · Cashfree · PhonePe · Paytm · BharatPe</p>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap" data-testid="pc-tabs">
        <TabPill active={tab === "dashboard"}    onClick={() => setTab("dashboard")}    testid="pc-tab-dashboard"   icon={ChartBar}>Dashboard</TabPill>
        <TabPill active={tab === "transactions"} onClick={() => setTab("transactions")} testid="pc-tab-transactions" icon={Receipt}>Live Transactions</TabPill>
        <TabPill active={tab === "providers"}    onClick={() => setTab("providers")}    testid="pc-tab-providers"   icon={Plug}>Providers</TabPill>
        <TabPill active={tab === "reports"}      onClick={() => setTab("reports")}      testid="pc-tab-reports"     icon={TrendUp}>Reports</TabPill>
      </div>

      {tab === "dashboard"    && <Dashboard />}
      {tab === "transactions" && <Transactions />}
      {tab === "providers"    && <Providers />}
      {tab === "reports"      && <Reports />}
    </div>
  );
}

// ============ Dashboard ============
function Dashboard() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [collectOpen, setCollectOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/payment-center/dashboard");
      setD(data);
    } catch (e) { toast.error("Could not load dashboard"); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 20000); // live refresh
    return () => clearInterval(t);
  }, []);

  const seedDemo = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payment-center/seed-demo");
      toast.success(data.message || `Seeded ${data.inserted || 0} demo transactions`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Seed failed"); }
    finally { setBusy(false); }
  };

  if (!d) return <div className="text-slate-500 py-20 text-center">Loading…</div>;

  const trend = (d.trend_7d || []).map((r) => ({ ...r, date: r.date.slice(5) }));
  const providerRows = Object.entries(d.by_provider || {}).map(([id, total]) => ({ id, total }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="pc-summary-cards">
        <BigStat label="Today's Collection" value={inr(d.today_total)} sub={`${d.today_count} txn today`} tone="orange" testid="pc-stat-today" />
        <BigStat label="UPI Collection"     value={inr(d.by_method?.UPI)} tone="emerald" testid="pc-stat-upi" />
        <BigStat label="Cash Collection"    value={inr(d.by_method?.Cash)} tone="amber" testid="pc-stat-cash" />
        <BigStat label="Card + Wallet"      value={inr((d.by_method?.Card || 0) + (d.by_method?.Wallet || 0))} tone="blue" testid="pc-stat-card" />
        <BigStat label="Pending / Failed"   value={`${d.pending_count} / ${d.failed_today}`} tone="red" sub="Pending · Failed today" testid="pc-stat-issues" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 bg-white border-slate-200 lg:col-span-2" data-testid="pc-trend-card">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">7-Day Collection Trend</div>
          {trend.length === 0 ? (
            <div className="text-center py-16 text-slate-400 text-sm">
              No data yet. <button onClick={seedDemo} disabled={busy} className="text-orange-600 underline ml-1">Seed demo data</button>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" fontSize={11} stroke="#64748b" />
                <YAxis fontSize={11} stroke="#64748b" tickFormatter={(v) => `₹${v / 1000}k`} />
                <Tooltip formatter={(v) => inr(v)} />
                <Bar dataKey="total" fill="#EA580C" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-5 bg-white border-slate-200" data-testid="pc-provider-card">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Today by Provider</div>
          {providerRows.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-xs">No collections yet today.</div>
          ) : (
            <div className="space-y-3">
              {providerRows.sort((a, b) => b.total - a.total).map((r) => (
                <div key={r.id}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="capitalize font-medium text-slate-700">{r.id}</span>
                    <span className="font-semibold text-slate-900">{inr(r.total)}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(100, (r.total / (d.today_total || 1)) * 100)}%`, backgroundColor: PROVIDER_COLOR[r.id] || "#EA580C" }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button onClick={() => setCollectOpen(true)} className="bg-orange-600 hover:bg-orange-700 text-white gap-2" data-testid="pc-collect-btn">
          <Plus size={16} weight="bold" /> Record Payment
        </Button>
        <Button variant="outline" onClick={load} className="gap-2"><ArrowClockwise size={14} /> Refresh</Button>
        <Button variant="outline" onClick={seedDemo} disabled={busy} className="gap-2 text-slate-500" data-testid="pc-seed-btn">
          <Sparkle size={13} /> {busy ? "Seeding…" : "Seed demo data"}
        </Button>
      </div>

      <CollectDialog open={collectOpen} onOpenChange={setCollectOpen} onSaved={load} />
    </div>
  );
}

// ============ Transactions ============
function Transactions() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [method, setMethod] = useState("_all");
  const [status, setStatus] = useState("_all");
  const [provider, setProvider] = useState("_all");
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payment-center/transactions", {
        params: {
          q: q || undefined,
          method: method === "_all" ? undefined : method,
          status: status === "_all" ? undefined : status,
          provider: provider === "_all" ? undefined : provider,
          days,
        },
      });
      setRows(data);
    } catch (e) { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [q, method, status, provider, days]);

  return (
    <Card className="p-4 bg-white border-slate-200" data-testid="pc-transactions-card">
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search customer, txn id, RRN…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 w-64" data-testid="pc-txn-search" />
        </div>
        <Select value={method} onValueChange={setMethod}>
          <SelectTrigger className="w-36 h-9" data-testid="pc-filter-method"><SelectValue placeholder="All methods" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All methods</SelectItem>
            {Object.keys(METHOD_TONES).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-36 h-9" data-testid="pc-filter-status"><SelectValue placeholder="All status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All status</SelectItem>
            {Object.keys(STATUS_TONES).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={provider} onValueChange={setProvider}>
          <SelectTrigger className="w-40 h-9" data-testid="pc-filter-provider"><SelectValue placeholder="All providers" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All providers</SelectItem>
            {["razorpay", "cashfree", "phonepe", "paytm", "bharatpe", "manual"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-28 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[1, 7, 30, 90].map((n) => <SelectItem key={n} value={String(n)}>{n === 1 ? "Today" : `${n} days`}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} className="gap-1.5 h-9"><ArrowClockwise size={13} /> Refresh</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100">
              <th className="text-left py-2">Time</th>
              <th className="text-left py-2">Customer</th>
              <th className="text-right py-2">Amount</th>
              <th className="text-left py-2">Method</th>
              <th className="text-left py-2">Provider</th>
              <th className="text-left py-2">Status</th>
              <th className="text-left py-2">Txn ID</th>
              <th className="text-left py-2">Operator</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && <tr><td colSpan={8} className="py-16 text-center text-slate-400">No transactions match the filters.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`pc-txn-row-${r.id}`}>
                <td className="py-2 text-xs">{r.created_at ? new Date(r.created_at).toLocaleString("en-IN", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                <td className="py-2 text-slate-900">{r.customer_name || <span className="text-slate-400">Walk-in</span>}</td>
                <td className="py-2 text-right font-semibold text-slate-900">{inr(r.amount)}</td>
                <td className="py-2"><Badge variant="outline" className={`text-[10px] ${METHOD_TONES[r.method] || METHOD_TONES.Other}`}>{r.method}</Badge></td>
                <td className="py-2 text-xs text-slate-700 capitalize">{r.provider}</td>
                <td className="py-2"><Badge variant="outline" className={`text-[10px] ${STATUS_TONES[r.status] || STATUS_TONES.Failed}`}>{r.status}</Badge></td>
                <td className="py-2 text-xs text-slate-500 font-mono">{r.transaction_id}</td>
                <td className="py-2 text-xs text-slate-500">{r.operator}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-slate-500 mt-3">{rows.length} transactions</div>
    </Card>
  );
}

// ============ Providers ============
function Providers() {
  const [state, setState] = useState({ available: [], connected: [] });
  const [dialog, setDialog] = useState(null); // provider id being connected
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/payment-providers"); setState(data); }
    catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    if (!form.provider) return;
    setSaving(true);
    try {
      await api.post("/payment-providers/connect", form);
      toast.success("Connected");
      setDialog(null); setForm({}); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const disconnect = async (provider) => {
    if (!window.confirm(`Disconnect ${provider}?`)) return;
    try { await api.delete(`/payment-providers/${provider}`); toast.success("Disconnected"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const connectedIds = new Set((state.connected || []).map((c) => c.provider));

  return (
    <div className="space-y-6" data-testid="pc-providers-panel">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Available Providers</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(state.available || []).map((p) => {
            const conn = (state.connected || []).find((c) => c.provider === p.id);
            return (
              <Card key={p.id} className="p-4 bg-white border-slate-200" data-testid={`pc-provider-card-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-slate-900 text-white flex items-center justify-center font-bold text-sm">{p.logo}</div>
                    <div>
                      <div className="font-semibold text-slate-900 text-sm">{p.label}</div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">{p.kind}</div>
                    </div>
                  </div>
                  {conn ? (
                    <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">● Connected</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-500 border-slate-200">○ Not connected</Badge>
                  )}
                </div>
                {conn && (
                  <div className="text-[11px] text-slate-500 mt-2">
                    <div>Mode: <span className="uppercase font-medium">{conn.mode}</span></div>
                    <div>Merchant: <span className="font-mono">{conn.merchant_id || "—"}</span></div>
                    <div>Key: <span className="font-mono">{conn.key_id_masked || "—"}</span></div>
                    <div className="text-slate-400 mt-1">Connected by {conn.connected_by}</div>
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  {conn ? (
                    <>
                      <Button size="sm" variant="outline" onClick={() => { setDialog(p.id); setForm({ provider: p.id, mode: conn.mode }); }} data-testid={`pc-edit-${p.id}`}>Update</Button>
                      <Button size="sm" variant="outline" onClick={() => disconnect(p.id)} className="text-red-600" data-testid={`pc-disc-${p.id}`}>Disconnect</Button>
                    </>
                  ) : (
                    <Button size="sm" onClick={() => { setDialog(p.id); setForm({ provider: p.id, mode: "test" }); }} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid={`pc-connect-${p.id}`}>Connect</Button>
                  )}
                  {p.docs && <a href={p.docs} target="_blank" rel="noreferrer" className="text-xs text-orange-600 hover:underline inline-flex items-center gap-1">Docs <ArrowSquareOut size={11} /></a>}
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="bg-white" data-testid="pc-connect-dialog">
          <DialogHeader><DialogTitle>Connect {state.available.find((p) => p.id === dialog)?.label}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Mode</Label>
              <Select value={form.mode || "test"} onValueChange={(v) => setForm({ ...form, mode: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="test">Test</SelectItem><SelectItem value="live">Live</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Key ID / Merchant Key</Label><Input value={form.key_id || ""} onChange={(e) => setForm({ ...form, key_id: e.target.value })} placeholder="rzp_live_XXXX or CF-XXXX" data-testid="pc-form-keyid" /></div>
            <div><Label>Key Secret</Label><Input type="password" value={form.key_secret || ""} onChange={(e) => setForm({ ...form, key_secret: e.target.value })} placeholder="Kept encrypted in DB" data-testid="pc-form-secret" /></div>
            <div><Label>Webhook Secret (optional)</Label><Input type="password" value={form.webhook_secret || ""} onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })} placeholder="For signature verification" /></div>
            <div><Label>Merchant ID (optional)</Label><Input value={form.merchant_id || ""} onChange={(e) => setForm({ ...form, merchant_id: e.target.value })} /></div>
            <div className="text-[11px] text-slate-500 italic">🔒 All credentials are stored with AES-256-GCM encryption. Only the last 4 chars of the Key ID are ever shown.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={connect} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="pc-form-save">{saving ? "Saving…" : "Save & Connect"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ Reports ============
function Reports() {
  const [kind, setKind] = useState("daily");
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payment-center/reports/summary", { params: { kind, days } });
      setRows(data.rows || []);
    } catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [kind, days]);

  const grouped = ["daily", "upi", "cash", "branch", "operator"].includes(kind);

  return (
    <div className="space-y-4" data-testid="pc-reports-panel">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mr-2">Report</div>
        {[
          ["daily",    "Daily Collection"],
          ["upi",      "UPI Collection"],
          ["cash",     "Cash Collection"],
          ["branch",   "Branch-wise"],
          ["operator", "Operator-wise"],
          ["pending",  "Pending"],
          ["failed",   "Failed"],
          ["refund",   "Refunds"],
        ].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setKind(id)}
            data-testid={`pc-report-${id}`}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${kind === id ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-slate-300"}`}
          >{label}</button>
        ))}
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-32 h-9 ml-2"><SelectValue /></SelectTrigger>
          <SelectContent>{[7, 30, 90, 180, 365].map((n) => <SelectItem key={n} value={String(n)}>{n} days</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Card className="p-4 bg-white border-slate-200">
        {rows.length === 0 && !loading && <div className="text-center py-16 text-slate-400 text-sm">No rows.</div>}
        {grouped && rows.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100">
                <th className="text-left py-2 capitalize">{kind === "daily" ? "Date" : kind === "upi" ? "Provider" : kind}</th>
                <th className="text-right py-2">Count</th>
                <th className="text-right py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.key || i} className="border-b border-slate-50">
                  <td className="py-2 text-slate-800 capitalize">{r.key || <span className="text-slate-400">—</span>}</td>
                  <td className="py-2 text-right text-slate-600">{r.count}</td>
                  <td className="py-2 text-right font-semibold">{inr(r.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!grouped && rows.length > 0 && (
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100">
              <th className="text-left py-2">Time</th><th className="text-left py-2">Customer</th><th className="text-right py-2">Amount</th><th className="text-left py-2">Method</th><th className="text-left py-2">Provider</th>
            </tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-50">
                <td className="py-2 text-xs">{r.created_at ? new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}</td>
                <td className="py-2">{r.customer_name}</td>
                <td className="py-2 text-right">{inr(r.amount)}</td>
                <td className="py-2">{r.method}</td>
                <td className="py-2 capitalize">{r.provider}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

// ============ Collect Dialog ============
function CollectDialog({ open, onOpenChange, onSaved }) {
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({ customer_id: "_none", amount: "", method: "UPI", provider: "manual", status: "Success", notes: "", reference_no: "", transaction_id: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {}); }, [open]);

  const submit = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Amount required"); return; }
    setSaving(true);
    try {
      await api.post("/payment-center/collect", {
        ...form,
        customer_id: form.customer_id === "_none" ? "" : form.customer_id,
        amount: Number(form.amount),
      });
      toast.success("Payment recorded");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg" data-testid="pc-collect-dialog">
        <DialogHeader><DialogTitle>Record a Payment</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Customer (optional)</Label>
            <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="_none">— Walk-in —</SelectItem>
                {customers.slice(0, 300).map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.mobile}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Amount (₹)</Label><Input type="number" min={1} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="pc-collect-amount" /></div>
            <div>
              <Label>Method</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(METHOD_TONES).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Provider</Label>
              <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["manual", "razorpay", "cashfree", "phonepe", "paytm", "bharatpe"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(STATUS_TONES).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Reference / RRN (optional)</Label><Input value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })} placeholder="RRN123456789012" /></div>
          <div><Label>Transaction ID (optional)</Label><Input value={form.transaction_id} onChange={(e) => setForm({ ...form, transaction_id: e.target.value })} placeholder="pay_abc123 or auto-generated" /></div>
          <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="pc-collect-save">{saving ? "Saving…" : "Record Payment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------- Small helpers --------
function TabPill({ active, onClick, children, icon: Icon, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm font-medium transition-colors ${active ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-100"}`}
    >
      {Icon && <Icon size={14} weight={active ? "fill" : "regular"} />} {children}
    </button>
  );
}

function BigStat({ label, value, sub, tone = "orange", testid }) {
  const toneMap = {
    orange:  "bg-orange-50 text-orange-800 border-orange-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    blue:    "bg-blue-50 text-blue-800 border-blue-200",
    red:     "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <Card className={`p-4 border ${toneMap[tone]} bg-white`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-semibold opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-1 text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </Card>
  );
}
